import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ThemeToggle from '../components/ThemeToggle';
import FooterModals from '../components/FooterModals';

const { FiShield, FiClock, FiUsers, FiArrowRight, FiHelpCircle } = FiIcons;

function Home() {
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: null
  });

  const openModal = (type) => {
    setModalState({ isOpen: true, type });
  };

  const closeModal = () => {
    setModalState({ isOpen: false, type: null });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-government-50 to-primary-50 dark:from-government-900 dark:to-government-800 transition-colors duration-300">
      {/* Header */}
      <header className="bg-white/80 dark:bg-government-900/80 backdrop-blur-sm border-b border-government-200 dark:border-government-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center space-x-3"
            >
              <div className="bg-primary-500 p-2 rounded-lg">
                <img 
                  src="https://res.cloudinary.com/frontly/image/upload/v1750023193/Untitled_design_eaqfik.svg" 
                  alt="AB Orders Logo" 
                  className="w-8 h-8" 
                />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-government-900 dark:text-white">AB Orders</h1>
                <p className="text-sm text-government-600 dark:text-government-300">Prison Pre-ordering System</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center space-x-4"
            >
              <nav className="hidden md:flex items-center space-x-6">
                <Link 
                  to="/how-it-works" 
                  className="flex items-center space-x-2 text-government-600 dark:text-government-300 hover:text-government-900 dark:hover:text-white transition-colors"
                >
                  <SafeIcon icon={FiHelpCircle} className="w-5 h-5" />
                  <span>How It Works</span>
                </Link>
              </nav>
              <ThemeToggle />
              <Link 
                to="/login" 
                className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center space-x-2"
              >
                <span>Login</span>
                <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h2 className="text-5xl font-bold text-government-900 dark:text-white mb-6">
              Streamline Prison Visits with{' '}
              <span className="text-primary-500 block">Pre-ordering</span>
            </h2>
            <p className="text-xl text-government-600 dark:text-government-300 mb-12 max-w-3xl mx-auto">
              Save precious visiting time with AB Orders. Pre-order food and beverages for your prison visit, 
              pay securely online, and collect on arrival. Approved by UK correctional facilities.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/login" 
                className="bg-primary-500 hover:bg-primary-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors duration-200 flex items-center space-x-2"
              >
                <span>Start Ordering</span>
                <img 
                  src="https://res.cloudinary.com/frontly/image/upload/v1750023193/Untitled_design_eaqfik.svg" 
                  alt="AB Orders" 
                  className="w-5 h-5" 
                />
              </Link>
              <Link 
                to="/how-it-works" 
                className="bg-white dark:bg-government-800 hover:bg-government-50 dark:hover:bg-government-700 text-government-700 dark:text-government-300 border-2 border-government-300 dark:border-government-600 px-8 py-4 rounded-lg font-semibold text-lg transition-colors duration-200 flex items-center space-x-2"
              >
                <SafeIcon icon={FiHelpCircle} className="w-5 h-5" />
                <span>How It Works</span>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white dark:bg-government-900 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center mb-16"
          >
            <h3 className="text-3xl font-bold text-government-900 dark:text-white mb-4">
              Why Choose AB Orders?
            </h3>
            <p className="text-government-600 dark:text-government-300 text-lg">
              Designed specifically for the UK prison system with government-grade security
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: FiClock,
                title: 'Save Time',
                description: 'Pre-order before your visit to maximize precious time with loved ones'
              },
              {
                icon: FiShield,
                title: 'Secure & Approved',
                description: 'Government-grade security with official prison system approval'
              },
              {
                icon: FiUsers,
                title: 'Easy Management',
                description: 'Simple inventory and order management tools for prison staff'
              },
              {
                icon: () => (
                  <img 
                    src="https://res.cloudinary.com/frontly/image/upload/v1750023193/Untitled_design_eaqfik.svg" 
                    alt="AB Orders" 
                    className="w-8 h-8" 
                  />
                ),
                title: 'Wide Selection',
                description: 'Fresh sandwiches, hot drinks, cold beverages and approved snacks'
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 + index * 0.1 }}
                className="bg-government-50 dark:bg-government-800 p-6 rounded-xl text-center hover:shadow-lg transition-shadow duration-300"
              >
                <div className="bg-primary-100 dark:bg-primary-900 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={feature.icon} className="w-8 h-8 text-primary-500 dark:text-primary-400" />
                </div>
                <h4 className="text-xl font-semibold text-government-900 dark:text-white mb-2">
                  {feature.title}
                </h4>
                <p className="text-government-600 dark:text-government-300">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-50 dark:bg-primary-900/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <h3 className="text-3xl font-bold text-government-900 dark:text-white mb-4">
              Ready to Streamline Your Prison Visit?
            </h3>
            <p className="text-xl text-government-600 dark:text-government-300 mb-8">
              Join thousands of families saving time with AB Orders pre-ordering system
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/login" 
                className="bg-primary-500 hover:bg-primary-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors duration-200 inline-flex items-center space-x-2"
              >
                <span>Get Started Today</span>
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </Link>
              <Link 
                to="/how-it-works" 
                className="bg-white dark:bg-government-800 hover:bg-government-50 dark:hover:bg-government-700 text-government-700 dark:text-government-300 border-2 border-government-300 dark:border-government-600 px-8 py-4 rounded-lg font-semibold text-lg transition-colors duration-200 inline-flex items-center space-x-2"
              >
                <SafeIcon icon={FiHelpCircle} className="w-5 h-5" />
                <span>Learn More</span>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-government-900 dark:bg-black text-white py-12 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="bg-primary-500 p-2 rounded-lg">
                <img 
                  src="https://res.cloudinary.com/frontly/image/upload/v1750023193/Untitled_design_eaqfik.svg" 
                  alt="AB Orders Logo" 
                  className="w-6 h-6" 
                />
              </div>
              <h3 className="text-xl font-bold">AB Orders</h3>
            </div>
            <p className="text-government-300 mb-4">
              Streamlining prison visits across the UK with secure, government-approved technology
            </p>
            <div className="flex justify-center space-x-8 text-sm text-government-400 mb-4">
              <Link to="/how-it-works" className="hover:text-white transition-colors">How It Works</Link>
              <button 
                onClick={() => openModal('privacy')}
                className="hover:text-white transition-colors"
              >
                Privacy Policy
              </button>
              <button 
                onClick={() => openModal('terms')}
                className="hover:text-white transition-colors"
              >
                Terms of Service
              </button>
              <button 
                onClick={() => openModal('support')}
                className="hover:text-white transition-colors"
              >
                Support
              </button>
              <button 
                onClick={() => openModal('contact')}
                className="hover:text-white transition-colors"
              >
                Contact
              </button>
            </div>
            <p className="text-government-400 text-sm">
              © 2025 AB Orders. All rights reserved. | Government Approved Prison Visitor System
            </p>
          </div>
        </div>
      </footer>

      {/* Footer Modals */}
      <FooterModals
        isOpen={modalState.isOpen}
        modalType={modalState.type}
        onClose={closeModal}
      />
    </div>
  );
}

export default Home;